import React, { useMemo } from 'react'
import { Avatar, Dropdown, Image, Typography } from 'antd'
import { Icon } from '@iconify/react'

import styles from './HomeCardHeader.module.css'
import headerStyles from '../../../layouts/header/header.module.css'

const { Text } = Typography

const TASK_TYPE_OPTIONS = [
    { label: 'Development', value: 'development' },
    { label: 'Support', value: 'support' },
]

const Kebab = ({ items }) => (
    <Dropdown trigger={['click']} placement="bottomRight" menu={{ items }}>
        <button type="button" className={styles.kebabButton} aria-label="More">
            <Icon icon="mdi:dots-horizontal" />
        </button>
    </Dropdown>
)

const renderTasksAvatarFallbackText = (userProfile) => {
    const un = userProfile?.userName
    if (un && String(un).trim()) {
        const s = String(un).trim()
        return (
            <>
                {(s[0] || 'U').toUpperCase()}
                {(s.split(/\s+/)?.[1]?.[0] || '').toUpperCase()}
            </>
        )
    }
    const raw =
        userProfile?.name || userProfile?.fullName || userProfile?.email || ''
    const parts = String(raw).trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
        return <>{(parts[0][0] + parts[parts.length - 1][0]).toUpperCase()}</>
    }
    const two = String(raw).trim().substring(0, 2).toUpperCase()
    return <>{two || 'U'}</>
}

const TasksCardHeader = ({
    userProfile,
    tasksTitle = 'My tasks',
    taskTab,
    onTaskTabChange,
    tabOptions = [],
    taskType,
    onTaskTypeChange,
    showTaskTypeDropdown,
}) => {
    const taskTypeMenuItems = useMemo(
        () =>
            TASK_TYPE_OPTIONS.map((option) => ({
                key: option.value,
                label: (
                    <div
                        className={`${styles.taskTypeMenuItem}${
                            taskType === option.value
                                ? ` ${styles.taskTypeMenuItemChecked}`
                                : ''
                        }`}
                    >
                        <span className={styles.taskTypeMenuCheck}>
                            {taskType === option.value ? '✓' : ''}
                        </span>
                        <span>{option.label}</span>
                    </div>
                ),
            })),
        [taskType]
    )

    return (
        <header className={styles.tasksCardHeader}>
            <div className={styles.tasksCardHeaderLeft}>
                <span
                    className={styles.tasksAvatarWrap}
                    title={
                        userProfile?.name ||
                        userProfile?.email ||
                        userProfile?.userName ||
                        ''
                    }
                >
                    {userProfile?.profilePicture ? (
                        <Image
                            src={userProfile.profilePicture}
                            height={48}
                            width={48}
                            className={`headerImage ${headerStyles.userAvatar}`}
                            style={{
                                borderRadius: '50%',
                            }}
                            preview={false}
                            alt=""
                        />
                    ) : (
                        <Avatar
                            size={48}
                            className={headerStyles.userAvatar}
                            style={{
                                backgroundColor: 'rgb(0 10 129 / 25%)',
                                minWidth: '36px',
                            }}
                        >
                            {userProfile
                                ? renderTasksAvatarFallbackText(userProfile)
                                : 'U'}
                        </Avatar>
                    )}
                </span>
                <div className={styles.tasksHeaderCopy}>
                    <div className={styles.tasksTitleRow}>
                        <Text className={styles.cardTitle}>{tasksTitle}</Text>
                        <Icon
                            icon="solar:lock-bold"
                            className={styles.tasksLockIcon}
                            aria-hidden
                        />
                    </div>
                    <nav
                        className={styles.tasksTabNav}
                        aria-label="Task filters"
                    >
                        {tabOptions.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`${styles.tasksTabBtn} ${
                                    taskTab === opt.value
                                        ? styles.tasksTabBtnActive
                                        : ''
                                }`}
                                onClick={() => onTaskTabChange?.(opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>
            {showTaskTypeDropdown ? (
                <div className={styles.tasksHeaderActions}>
                    <Dropdown
                        trigger={['click']}
                        overlayClassName={styles.taskTypeDropdown}
                        menu={{
                            items: taskTypeMenuItems,
                            onClick: ({ key }) => onTaskTypeChange?.(key),
                        }}
                    >
                        <button
                            type="button"
                            className={styles.taskTypeTrigger}
                        >
                            <span className={styles.taskTypeLabel}>
                                {taskType === 'support'
                                    ? 'Support'
                                    : 'Development'}
                            </span>
                            <Icon
                                icon="mdi:chevron-down"
                                className={styles.taskTypeChevron}
                            />
                        </button>
                    </Dropdown>
                </div>
            ) : null}
        </header>
    )
}

const HomeCardHeader = ({
    title,
    rightLabel,
    menuItems = [],
    variant = 'default',
    tasksTitle,
    userProfile,
    taskTab,
    onTaskTabChange,
    tabOptions,
    taskType,
    onTaskTypeChange,
    showTaskTypeDropdown,
}) => {
    if (variant === 'tasks') {
        return (
            <TasksCardHeader
                userProfile={userProfile}
                tasksTitle={tasksTitle}
                taskTab={taskTab}
                onTaskTabChange={onTaskTabChange}
                tabOptions={tabOptions}
                taskType={taskType}
                onTaskTypeChange={onTaskTypeChange}
                showTaskTypeDropdown={showTaskTypeDropdown}
            />
        )
    }

    return (
        <div className={styles.cardHeader}>
            <Text className={styles.cardTitle}>{title}</Text>
            <div className={styles.rightMeta}>
                {rightLabel ? (
                    <Text className={styles.recents}>{rightLabel}</Text>
                ) : null}
                {rightLabel ? (
                    <Icon icon="mdi:chevron-down" className={styles.chevron} />
                ) : null}
                {menuItems.length > 0 ? (
                    <Kebab items={menuItems} />
                ) : null}
            </div>
        </div>
    )
}

export default HomeCardHeader
